import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { theme } from "@src/theme";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  acceptConversation,
  blockConversation,
  unblockConversation,
  endConversation,
} from "@src/apis/curatorConversationApis";
import { useIVSChat } from "@src/hooks/useIVSChat";
import SendMessageIcon from "@src/assets/img/send-message.svg";
import Spinner from "@src/components/Spinner";
import UIModal from "@src/components/UIModal/UIModal";
import { UIButton } from "@src/components/UIButton";
import {
  getDisplayMessages,
  getEmptyChatHint,
  getClientDisplayName,
  getConversationSubject,
  getConversationPlaylistSource,
  formatMessageTimestamp,
} from "./conversationUtils";
import {
  ICuratorConversation,
  ICuratorConversationStatus,
} from "@src/types/curator";
import type { MessagesTab } from "./conversationUtils";
import {
  FIGMA_MESSAGE_TEXT,
  FIGMA_USER_NAME_PILL,
  FIGMA_CHAT_MESSAGE_MAX_WIDTH,
  FIGMA_CHAT_COLUMN_MAX_WIDTH,
  FIGMA_BLOCKED_MUTED_OPACITY,
  FIGMA_ACTION_BUTTON_WIDTH,
  FIGMA_ACTION_BUTTON_HEIGHT,
} from "./MessagesPage.styles";
import { ChatPanelEmptyState } from "./MessagesEmptyState";

type PendingChatAction = "block" | "end";

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.darkBg};
  min-height: 0;
  min-width: 0;
`;

const ChatPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 32px 40px 28px;
`;

const ChatContentColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  max-width: ${FIGMA_CHAT_COLUMN_MAX_WIDTH};
  margin: 0 auto;
`;

const CuratorNamePill = styled.div`
  background-color: ${theme.colors.yellow};
  color: ${theme.colors.black};
  border: 1px solid ${theme.colors.yellow};
  border-radius: 13px;
  padding: 1px 11px;
  font-size: 15.4px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.02em;
  margin-bottom: 10px;
  width: fit-content;
`;

const UserNamePill = styled.div`
  background-color: ${FIGMA_USER_NAME_PILL};
  color: ${theme.colors.black};
  border: 1px solid ${FIGMA_USER_NAME_PILL};
  border-radius: 13px;
  padding: 1px 11px;
  font-size: 15.4px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.02em;
  margin-bottom: 10px;
  width: fit-content;
`;

const MessagesArea = styled.div<{
  $readOnly?: boolean;
  $blockedMuted?: boolean;
}>`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  opacity: ${(props) => {
    if (props.$blockedMuted) return FIGMA_BLOCKED_MUTED_OPACITY;
    if (props.$readOnly) return 0.88;
    return 1;
  }};

  &::-webkit-scrollbar {
    width: 9px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.43);
    border-radius: 999px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.43) transparent;
`;

const MessagesInner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const MessageRow = styled.div<{ $isSelf: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isSelf ? "flex-end" : "flex-start")};
  align-self: ${(props) => (props.$isSelf ? "flex-end" : "flex-start")};
  max-width: ${FIGMA_CHAT_MESSAGE_MAX_WIDTH};
  width: fit-content;
`;

const MessageText = styled.p<{ $isSelf: boolean }>`
  color: ${FIGMA_MESSAGE_TEXT};
  font-size: 15.4px;
  line-height: ${(props) => (props.$isSelf ? "22px" : "30px")};
  margin: 0;
  word-break: break-word;
  text-align: ${(props) => (props.$isSelf ? "right" : "left")};
  width: 100%;
`;

const MessageTime = styled.span<{ $isSelf: boolean }>`
  color: ${FIGMA_MESSAGE_TEXT};
  opacity: 0.5;
  font-size: 10px;
  line-height: 19px;
  letter-spacing: -0.02em;
  margin-top: 8px;
  align-self: ${(props) => (props.$isSelf ? "flex-end" : "flex-start")};
`;

const ChatFooter = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding-top: 24px;
  flex-shrink: 0;
`;

const ChatFooterRow = styled.div`
  position: relative;
  width: 100%;
  max-width: ${FIGMA_CHAT_COLUMN_MAX_WIDTH};
  margin: 0 auto;
`;

const ChatFooterMain = styled.div`
  width: 100%;
  box-sizing: border-box;
  /* Reserve room for the absolutely-positioned action column so the input /
     status bar never sits underneath it (e.g. Requests "Accept request to
     reply" overlapping Block). */
  padding-right: calc(${FIGMA_ACTION_BUTTON_WIDTH} + 24px);
`;

const ChatFooterActions = styled.div`
  position: absolute;
  right: 0;
  bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 13px;
  align-items: flex-end;
`;

const InputFieldWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input<{ $muted?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  background-color: transparent;
  border: 1.5px solid ${theme.colors.yellow};
  border-radius: 100px;
  min-height: 44px;
  padding: 10px 48px 10px 20px;
  color: ${theme.colors.white};
  font-size: 15.4px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.02em;
  outline: none;
  &::placeholder {
    color: ${theme.colors.yellow};
    font-weight: 700;
  }
  &:focus {
    border-color: ${theme.colors.yellow};
    box-shadow: 0 0 0 1px ${theme.colors.yellow};
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const MutedInputWrap = styled.div`
  opacity: ${FIGMA_BLOCKED_MUTED_OPACITY};
`;

const SendButton = styled.button<{ $muted?: boolean }>`
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: ${(props) => (props.$muted ? 1 : 1)};
  &:disabled {
    cursor: not-allowed;
  }
`;

const ActionButtonsContainer = styled.div`
  display: contents;
`;

const ActionButton = styled.button<{ $variant?: "danger" | "warning" }>`
  background: transparent;
  border: 1px solid ${theme.colors.yellow};
  color: ${theme.colors.yellow};
  border-radius: 100px;
  width: ${FIGMA_ACTION_BUTTON_WIDTH};
  height: ${FIGMA_ACTION_BUTTON_HEIGHT};
  padding: 0 10px;
  cursor: pointer;
  font-size: 15.4px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: rgba(255, 240, 87, 0.1);
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray150};
`;

const ChatEmptyHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${FIGMA_MESSAGE_TEXT};
  font-size: 15.4px;
  text-align: center;
  padding: 40px 0;
  max-width: ${FIGMA_CHAT_MESSAGE_MAX_WIDTH};
  margin: 0 auto;
  line-height: 30px;
  letter-spacing: -0.02em;
`;

const ChatContextHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 0 0 28px;
  border-bottom: 1px solid rgba(206, 206, 206, 0.12);
  margin-bottom: 8px;
`;

const ChatContextPeerName = styled.h3`
  margin: 0;
  color: ${theme.colors.yellow};
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.02em;
`;

const ChatContextMeta = styled.p`
  margin: 0;
  color: ${FIGMA_MESSAGE_TEXT};
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.02em;
  max-width: ${FIGMA_CHAT_MESSAGE_MAX_WIDTH};
`;

const ChatStatusBar = styled.div<{
  $variant?: "default" | "blocked" | "ended";
}>`
  width: 100%;
  min-height: 44px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 100px;
  font-size: 15.4px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${FIGMA_MESSAGE_TEXT};
  border: 1px solid
    ${(props) => {
      if (props.$variant === "blocked") return "rgba(255, 59, 48, 0.45)";
      if (props.$variant === "ended") return "rgba(255, 240, 87, 0.45)";
      return "rgba(255, 240, 87, 0.45)";
    }};
  background: ${(props) => {
    if (props.$variant === "blocked") return "rgba(255, 59, 48, 0.08)";
    if (props.$variant === "ended") return "rgba(255, 240, 87, 0.05)";
    return "rgba(255, 240, 87, 0.05)";
  }};
`;

const EndedChatStatusBar = styled(ChatStatusBar).attrs({ $variant: "ended" })``;

interface ChatViewProps {
  conversationId: string | null;
  conversation?: Partial<ICuratorConversation>;
  conversationStatus?: ICuratorConversationStatus;
  curatorId?: number;
  curatorDocumentId?: string;
  curatorName?: string;
  activeTab: MessagesTab;
  allowMessages?: boolean;
  onConversationAccepted?: () => void;
  onConversationRead?: (conversationId: string) => void;
  onLiveMessage?: () => void;
}

const ChatView: React.FC<ChatViewProps> = ({
  conversationId,
  conversation,
  conversationStatus,
  curatorId,
  curatorDocumentId,
  curatorName,
  activeTab,
  allowMessages = true,
  onConversationAccepted,
  onConversationRead,
  onLiveMessage,
}) => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingChatAction | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const liveMessageDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (liveMessageDebounceRef.current) {
        clearTimeout(liveMessageDebounceRef.current);
      }
    };
  }, []);

  const handleLiveMessage = useCallback(() => {
    if (!onLiveMessage) return;
    if (liveMessageDebounceRef.current) {
      clearTimeout(liveMessageDebounceRef.current);
    }
    liveMessageDebounceRef.current = setTimeout(() => {
      onLiveMessage();
    }, 400);
  }, [onLiveMessage]);

  const enableRealtime =
    conversationStatus === "active" && activeTab === "inbox";

  const {
    messages,
    isLoadingHistory,
    isConnected,
    isConnecting,
    error: chatError,
    sendMessage,
  } = useIVSChat(conversationId, {
    enableRealtime,
    onLiveMessage: handleLiveMessage,
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Requests count as read once the curator opens them, even if they leave
  // without accepting — otherwise the Requests badge and the row indicator stay
  // lit forever. Blocked / AI Blocked stay untouched: those are never "read".
  useEffect(() => {
    if (
      !conversationId ||
      activeTab === "blocked" ||
      activeTab === "ai_blocked"
    ) {
      return;
    }

    onConversationRead?.(conversationId);
  }, [activeTab, conversationId, onConversationRead]);

  useEffect(() => {
    setPendingAction(null);
  }, [conversationId]);

  const closeConfirmModal = () => setPendingAction(null);

  const acceptMutation = useMutation({
    mutationFn: () => acceptConversation(conversationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      onConversationAccepted?.();
    },
    onError: () => {
      window.alert("Failed to accept conversation");
    },
  });

  const blockMutation = useMutation({
    mutationFn: () => blockConversation(conversationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: () => {
      window.alert("Failed to block conversation");
    },
  });

  const unblockMutation = useMutation({
    mutationFn: () => unblockConversation(conversationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: () => {
      window.alert("Failed to unblock conversation");
    },
  });

  const endMutation = useMutation({
    mutationFn: () => endConversation(conversationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: () => {
      window.alert("Failed to end conversation");
    },
  });

  const handleConfirmAction = () => {
    if (!pendingAction) return;

    if (pendingAction === "block") {
      blockMutation.mutate();
    } else {
      endMutation.mutate();
    }

    closeConfirmModal();
  };

  if (!conversationId) {
    return (
      <ChatContainer>
        <ChatPanel>
          <ChatContentColumn>
            <MessagesArea>
              <ChatPanelEmptyState />
            </MessagesArea>
          </ChatContentColumn>
        </ChatPanel>
      </ChatContainer>
    );
  }

  if (isLoadingHistory && messages.length === 0) {
    return (
      <EmptyState>
        <Spinner />
      </EmptyState>
    );
  }

  const isEnded = conversationStatus === "ended";
  const isEndedView = activeTab === "ended" || isEnded;
  const isBlockedView = activeTab === "blocked" || activeTab === "ai_blocked";
  const isReadOnlyView = isEndedView;
  const canReply =
    allowMessages && activeTab === "inbox" && conversationStatus === "active";
  const peerName = conversation
    ? getClientDisplayName(conversation)
    : "this user";
  const conversationSubject = conversation
    ? getConversationSubject(conversation)
    : null;
  const conversationPlaylistSource = conversation
    ? getConversationPlaylistSource(conversation)
    : null;

  const confirmModalCopy =
    pendingAction === "block"
      ? {
          title: "Block this conversation?",
          body: `Block ${peerName}? They will not be able to message you again until you unblock them.`,
          confirmLabel: "Block",
        }
      : pendingAction === "end"
        ? {
            title: "End this chat?",
            body: `End the conversation with ${peerName}? They will no longer be able to send new messages.`,
            confirmLabel: "End Chat",
          }
        : {
            title: "",
            body: "",
            confirmLabel: "Confirm",
          };

  const displayMessages = getDisplayMessages(
    messages as Record<string, unknown>[],
    conversation,
    { curatorId, curatorDocumentId, curatorName }
  );

  const emptyChatHint = getEmptyChatHint(conversation, activeTab);
  const showConversationContext = activeTab === "requests";

  const renderMutedMessageInput = (tooltip: string) => (
    <MutedInputWrap>
      <InputFieldWrapper>
        <Input
          placeholder="Write a message"
          value=""
          readOnly
          disabled
          title={tooltip}
        />
        <SendButton disabled title={tooltip} $muted>
          <img
            src={SendMessageIcon}
            alt="Send"
            width={18}
            height={18}
            style={{ opacity: 1 }}
          />
        </SendButton>
      </InputFieldWrapper>
    </MutedInputWrap>
  );

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || !canReply) return;

    setMessage("");

    try {
      await sendMessage(trimmed);
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    } catch {
      setMessage(trimmed);
      window.alert("Failed to send message. Check your chat connection.");
    }
  };

  const renderActionButtons = () => {
    if (isEndedView) {
      return null;
    }

    switch (activeTab) {
      case "requests":
        return (
          <ActionButtonsContainer>
            {allowMessages && (
              <ActionButton
                $variant="warning"
                onClick={() => acceptMutation.mutate()}
              >
                Accept
              </ActionButton>
            )}
            <ActionButton onClick={() => setPendingAction("block")}>
              Block
            </ActionButton>
          </ActionButtonsContainer>
        );
      case "inbox":
        return (
          <ActionButtonsContainer>
            <ActionButton onClick={() => setPendingAction("block")}>
              Block
            </ActionButton>
            <ActionButton onClick={() => setPendingAction("end")}>
              End Chat
            </ActionButton>
          </ActionButtonsContainer>
        );
      case "blocked":
      case "ai_blocked":
        return (
          <ActionButtonsContainer>
            <ActionButton
              $variant="warning"
              onClick={() => unblockMutation.mutate()}
            >
              Unblock
            </ActionButton>
          </ActionButtonsContainer>
        );
      default:
        return null;
    }
  };

  return (
    <ChatContainer>
      <UIModal
        isOpen={pendingAction !== null}
        onClose={closeConfirmModal}
        title={confirmModalCopy.title}
        actions={[
          <UIButton
            key="cancel"
            onClick={closeConfirmModal}
            className="secondary"
          >
            Cancel
          </UIButton>,
          <UIButton key="confirm" onClick={handleConfirmAction}>
            {confirmModalCopy.confirmLabel}
          </UIButton>,
        ]}
      >
        <p style={{ margin: 0, color: theme.colors.white, lineHeight: 1.5 }}>
          {confirmModalCopy.body}
        </p>
      </UIModal>
      <ChatPanel>
        <ChatContentColumn>
          <MessagesArea
            $readOnly={isReadOnlyView}
            $blockedMuted={isBlockedView}
          >
            <MessagesInner>
              {showConversationContext && (
                <ChatContextHeader>
                  <ChatContextPeerName>{peerName}</ChatContextPeerName>
                  {conversationSubject && (
                    <ChatContextMeta>
                      Subject: {conversationSubject}
                    </ChatContextMeta>
                  )}
                  {conversationPlaylistSource && (
                    <ChatContextMeta>
                      From: {conversationPlaylistSource}
                    </ChatContextMeta>
                  )}
                </ChatContextHeader>
              )}
              {displayMessages.length === 0 && (
                <ChatEmptyHint>{emptyChatHint}</ChatEmptyHint>
              )}
              {displayMessages.map((normalized) => (
                <MessageRow key={normalized.id} $isSelf={normalized.isSelf}>
                  {normalized.isCurator ? (
                    <CuratorNamePill>{normalized.senderName}</CuratorNamePill>
                  ) : (
                    <UserNamePill>{normalized.senderName}</UserNamePill>
                  )}
                  <MessageText $isSelf={normalized.isSelf}>
                    {normalized.content}
                  </MessageText>
                  <MessageTime $isSelf={normalized.isSelf}>
                    {formatMessageTimestamp(normalized.createdAt)}
                  </MessageTime>
                </MessageRow>
              ))}
              <div ref={messagesEndRef} />
            </MessagesInner>
          </MessagesArea>
        </ChatContentColumn>

        <ChatFooter>
          <ChatFooterRow>
            <ChatFooterMain>
              {activeTab === "requests" ? (
                <ChatStatusBar>
                  {allowMessages
                    ? "Accept request to reply"
                    : "Turn on Allow Messages to accept requests"}
                </ChatStatusBar>
              ) : isBlockedView ? (
                renderMutedMessageInput("Unblock to send messages")
              ) : isEndedView ? (
                <EndedChatStatusBar>
                  This conversation has ended
                </EndedChatStatusBar>
              ) : (
                <InputFieldWrapper>
                  {isConnecting && allowMessages && (
                    <div
                      style={{
                        position: "absolute",
                        top: -22,
                        left: 0,
                        color: theme.colors.gray150,
                        fontSize: "0.85rem",
                      }}
                    >
                      Connecting to chat...
                    </div>
                  )}
                  {!isConnecting &&
                    !isConnected &&
                    chatError &&
                    allowMessages && (
                      <div
                        style={{
                          position: "absolute",
                          top: -22,
                          left: 0,
                          color: theme.colors.yellow,
                          fontSize: "0.85rem",
                        }}
                      >
                        {chatError}
                      </div>
                    )}
                  <Input
                    placeholder="Write a message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSend();
                    }}
                    disabled={!allowMessages || !isConnected}
                    readOnly={!allowMessages}
                  />
                  <SendButton
                    onClick={handleSend}
                    disabled={
                      !allowMessages ||
                      !message.trim() ||
                      !isConnected ||
                      !canReply
                    }
                    title={
                      !allowMessages
                        ? "Turn on Allow Messages to reply"
                        : !isConnected
                          ? isConnecting
                            ? "Connecting..."
                            : "Chat not connected"
                          : "Send message"
                    }
                  >
                    <img
                      src={SendMessageIcon}
                      alt="Send"
                      width={18}
                      height={18}
                      style={{ opacity: allowMessages ? 1 : 0.35 }}
                    />
                  </SendButton>
                </InputFieldWrapper>
              )}
            </ChatFooterMain>
            <ChatFooterActions>{renderActionButtons()}</ChatFooterActions>
          </ChatFooterRow>
        </ChatFooter>
      </ChatPanel>
    </ChatContainer>
  );
};

export default ChatView;
